import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from '../redux/reducers/mapReducer';
import GPXUpload from '../components/GPXUpload';
import { parseGPX } from '../services/gpx';
import { snapToRoads } from '../services/google';

const GPXUploadContainer = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const { gpxPoints, isSnapped } = useSelector((state) => state.map.data);

  const handleFileUpload = async (file) => {
    setIsLoading(true);
    try {
      const gpxPoints = await parseGPX(file);

      // Extract coordinates for map display
      const coordinates = gpxPoints.map((point) => ({ lat: point.lat, lng: point.lng }));

      // Calculate center point for map view
      const centerLat = coordinates.reduce((sum, coord) => sum + coord.lat, 0) / coordinates.length;
      const centerLng = coordinates.reduce((sum, coord) => sum + coord.lng, 0) / coordinates.length;

      // Just set the GPX data, don't call Snap to Roads API yet
      dispatch(
        setData({
          gpxPoints: gpxPoints,
          gpxCoordinates: coordinates,
          snappedCoordinates: [],
          mapCenter: { lat: centerLat, lng: centerLng },
          error: '',
          isSnapped: false,
        }),
      );
    } catch (error) {
      console.error('Failed to parse GPX file:', error);
      dispatch(
        setData({
          gpxPoints: [],
          gpxCoordinates: [],
          snappedCoordinates: [],
          error: error.message || 'Failed to parse GPX file',
          isSnapped: false,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnapToRoads = async () => {
    if (!gpxPoints || gpxPoints.length === 0) {
      return;
    }

    setIsSnapping(true);
    try {
      // Extract coordinates for Snap to Roads API
      const coordinates = gpxPoints.map((point) => ({ lat: point.lat, lng: point.lng }));

      // Note: API has a limit of 100 points, so we may need to sample or batch
      const pointsToSnap =
        coordinates.length > 100
          ? coordinates.filter((_, i) => i % Math.ceil(coordinates.length / 100) === 0)
          : coordinates;

      const snappedCoordinates = await snapToRoads(pointsToSnap, true);

      dispatch(
        setData({
          snappedCoordinates: snappedCoordinates,
          isSnapped: true,
          error: '',
        }),
      );
    } catch (error) {
      console.error('Failed to snap to roads:', error);
      dispatch(
        setData({
          error: error.message || 'Failed to snap to roads',
        }),
      );
    } finally {
      setIsSnapping(false);
    }
  };

  return (
    <GPXUpload
      onFileUpload={handleFileUpload}
      onSnapToRoads={handleSnapToRoads}
      isLoading={isLoading}
      isSnapping={isSnapping}
      hasGPXData={gpxPoints && gpxPoints.length > 0}
      isSnapped={isSnapped}
    />
  );
};

export default GPXUploadContainer;
